import React from "react";
import { Link } from "react-router-dom";
import { Container, Logo } from "./ui";

// Navy, four columns, per design system §15.
const COLUMNS = [
  {
    heading: "Explore",
    links: [
      { to: "/rankings", label: "Rankings" },
      { to: "/searchTournaments", label: "Tournaments" },
      { to: "/clubs", label: "Clubs" },
    ],
  },
  {
    heading: "Platform",
    links: [
      { to: "/how-rankings-work", label: "How Rankings Work" },
      { to: "/ask", label: "Ask AI" },
      { to: "/register", label: "Get Ranked" },
    ],
  },
  {
    heading: "Company",
    links: [
      { to: "/how-rankings-work", label: "About" },
      { to: "/how-rankings-work", label: "Contact" },
      { to: "/how-rankings-work", label: "Privacy" },
      { to: "/how-rankings-work", label: "Terms" },
    ],
  },
];

const SOCIALS = [
  {
    label: "X",
    href: "https://x.com",
    path: "M18.9 3H21l-6.6 7.6L22 21h-6.1l-4.8-6.3L5.6 21H3.5l7-8.1L2 3h6.2l4.4 5.8ZM17.8 19.4h1.2L7.3 4.5H6z",
  },
  {
    label: "Instagram",
    href: "https://instagram.com",
    path: "M12 7.4A4.6 4.6 0 1 0 16.6 12 4.6 4.6 0 0 0 12 7.4Zm0 7.6a3 3 0 1 1 3-3 3 3 0 0 1-3 3Zm5.9-7.8a1.07 1.07 0 1 1-1.07-1.07A1.07 1.07 0 0 1 17.9 7.2ZM21 8.2a5.3 5.3 0 0 0-1.45-3.75A5.3 5.3 0 0 0 15.8 3C14.3 2.92 9.7 2.92 8.2 3a5.3 5.3 0 0 0-3.75 1.44A5.3 5.3 0 0 0 3 8.2c-.08 1.5-.08 6.1 0 7.6a5.3 5.3 0 0 0 1.45 3.75A5.3 5.3 0 0 0 8.2 21c1.5.08 6.1.08 7.6 0a5.3 5.3 0 0 0 3.75-1.45A5.3 5.3 0 0 0 21 15.8c.08-1.5.08-6.1 0-7.6Zm-1.9 9.2a3 3 0 0 1-1.7 1.7c-1.18.47-4 .36-5.4.36s-4.22.1-5.4-.36a3 3 0 0 1-1.7-1.7c-.47-1.18-.36-4-.36-5.4s-.11-4.22.36-5.4a3 3 0 0 1 1.7-1.7c1.18-.47 4-.36 5.4-.36s4.22-.1 5.4.36a3 3 0 0 1 1.7 1.7c.47 1.18.36 4 .36 5.4s.11 4.22-.36 5.4Z",
  },
  {
    label: "Facebook",
    href: "https://facebook.com",
    path: "M22 12a10 10 0 1 0-11.56 9.88v-7H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.77-3.89a15.4 15.4 0 0 1 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.45 2.88h-2.33v7A10 10 0 0 0 22 12Z",
  },
  {
    label: "YouTube",
    href: "https://youtube.com",
    path: "M21.6 7.2a2.5 2.5 0 0 0-1.77-1.78C18.25 5 12 5 12 5s-6.25 0-7.83.42A2.5 2.5 0 0 0 2.4 7.2 26 26 0 0 0 2 12a26 26 0 0 0 .4 4.8 2.5 2.5 0 0 0 1.77 1.78C5.75 19 12 19 12 19s6.25 0 7.83-.42a2.5 2.5 0 0 0 1.77-1.78A26 26 0 0 0 22 12a26 26 0 0 0-.4-4.8ZM10 15.02V8.98L15.2 12Z",
  },
];

const Footer = () => (
  <footer className="bg-navy text-white">
    <Container className="py-14 md:py-16">
      <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_1fr]">
        <div>
          <Logo to={null} inverted />
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-white/60">
            The global platform for padel rankings and competition.
          </p>
          <div className="mt-6 flex gap-3">
            {SOCIALS.map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors hover:border-white/40 hover:text-white"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                  <path d={social.path} />
                </svg>
              </a>
            ))}
          </div>
        </div>

        {COLUMNS.map((column) => (
          <div key={column.heading}>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-white/50">
              {column.heading}
            </h2>
            <ul className="mt-4 space-y-3">
              {column.links.map((link) => (
                <li key={`${column.heading}-${link.label}`}>
                  <Link
                    to={link.to}
                    className="text-sm text-white/75 transition-colors hover:text-white"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="mt-12 border-t border-white/10 pt-6 text-sm text-white/50">
        © {new Date().getFullYear()} PadelRank
      </div>
    </Container>
  </footer>
);

export default Footer;
